import { Fragment } from 'react';
import gettext from '@sdk/lang/gettext';
import H5 from '@sdk/components/ui/typography/H5';
import Text14 from '@sdk/components/ui/typography/Text14';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Checkbox from '@sdk/components/forms/Checkbox';
import PickerField from '@sdk/components/forms/PickerField';
import PickerFieldBodyPortal from '@sdk/components/forms/PickerFieldBodyPortal';
import countries from '@sdk/config/countries';

import {
  MapColumnsContainer,
  CheckboxLabel,
  FormField,
  MapColumnsCell,
  MapColumnsCellText,
} from './mapColumns.styles';
import { useMapColumns } from './mapColumns.hooks';

const countryOptions = countries.map(({ label, value, code }) => ({
  label,
  value: `${value}:${code}`,
}));

type Props = {
  updateExistingLocations: boolean;
  setUpdateExistingLocations: (value: boolean) => void;
  defaultCountryCode: string;
  setDefaultCountryCode: (value: string) => void;
  parsedFileData: string[][];
  columnMap: Record<number, string>;
  setColumnMap: (value: Record<number, string>) => void;
  rowSelection: Record<number, boolean>;
  setRowSelection: (value: Record<number, boolean>) => void;
  pickedColumnsDict: Record<string, boolean>;
};

const MapColumns = ({
  updateExistingLocations,
  setUpdateExistingLocations,
  defaultCountryCode,
  setDefaultCountryCode,
  parsedFileData,
  columnMap,
  setColumnMap,
  rowSelection,
  setRowSelection,
  pickedColumnsDict,
}: Props) => {
  const { isAllSelected, toggleAllSelected, onRowSelectionChange, columnOptions } = useMapColumns({
    parsedFileData,
    columnMap,
    setColumnMap,
    rowSelection,
    setRowSelection,
    defaultCountryCode,
    setDefaultCountryCode,
    pickedColumnsDict,
  });

  return (
    <>
      <Column mBottom={20}>
        <H5 themeColor={ThemeColorType.primary}>
          {gettext('Match column labels to your data and choose fields to import')}
        </H5>
      </Column>
      <MapColumnsContainer cols={parsedFileData[0]}>
        <Checkbox value={isAllSelected()} onChange={toggleAllSelected} />
        {columnMap &&
          parsedFileData[0].map((cell, index) => {
            return (
              <MapColumnsCell key={index}>
                <PickerFieldBodyPortal
                  options={columnOptions}
                  placeholder={gettext('Choose label...')}
                  value={columnMap[index]}
                  dropdownAutoWidth
                  inactiveOptions={pickedColumnsDict}
                  showClearOption
                  onChange={(value: string) => {
                    setColumnMap({
                      ...columnMap,
                      [index]: value || null,
                    });
                  }}
                />
              </MapColumnsCell>
            );
          })}
        {rowSelection &&
          parsedFileData.map((row, rowIndex) => {
            return (
              <Fragment key={rowIndex}>
                <Checkbox
                  value={rowSelection[rowIndex]}
                  onChange={() => onRowSelectionChange(rowIndex)}
                />
                {parsedFileData[0].map((cell, colIndex) => {
                  return (
                    <MapColumnsCell
                      key={colIndex}
                      inactive={!rowSelection[rowIndex] || !columnMap[colIndex]}
                      withBorder={rowIndex < parsedFileData.length - 1}>
                      <MapColumnsCellText>{row[colIndex] || '\xa0'}</MapColumnsCellText>
                    </MapColumnsCell>
                  );
                })}
              </Fragment>
            );
          })}
      </MapColumnsContainer>
      <Row pTop={20}>
        <Checkbox value={updateExistingLocations} onChange={setUpdateExistingLocations} />
        <CheckboxLabel
          onClick={() => {
            setUpdateExistingLocations(!updateExistingLocations);
          }}>
          {gettext('Update existing locations')}
        </CheckboxLabel>
      </Row>
      {Boolean(pickedColumnsDict.phone) && (
        <Row pTop={20} alignItems="center">
          <Text14 themeColor={ThemeColorType.gray}>
            {gettext("Default country code for newly created location's phone number")}
          </Text14>
          <FormField>
            <PickerField
              options={countryOptions}
              value={defaultCountryCode}
              showMenuAbove
              onChange={(opt: string) => {
                setDefaultCountryCode(opt);
              }}
            />
          </FormField>
        </Row>
      )}
    </>
  );
};

export default MapColumns;
